import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Sidebar, Snackbar } from '@agco-onedigital/ecosystem-lib-web-components';
import { EAuthRoutes } from '@agco-onedigital/ecosystem-lib-frontend-utils';
import { commonMessages } from 'messages';
import { useAppDispatch } from '../../store';
import { setActiveAccount, useUserAccount } from '../../store/UserAccount';
import { saveUserThunk, useUser } from '../../store/User';
import { useLaunchDarkly } from '../../store/LaunchDarkly';
import { resetMachineList } from '../../store/MachineList';
import { handleLogoutRedirect } from '../../utils/msalConfiguration/utils';
import { useDtrumUserSetup } from '../../utils/dtrumUserSetup';
import { FEATURE_MAPPINGS } from '../../utils/features/featureMapping';
import { useFeatures } from '../../utils/hooks/useFeatures';
import { ISidebarProps } from './sidebar/types';
import { useSidebarProps } from './sidebar/useSidebarProps';
import { SupportActivityBadgeStyle } from './styles';

import { useServiceInformationLinks } from './utils';

const ROUTE_CHECK_PATHS = [EAuthRoutes.SERVICE_AND_INFORMATION, EAuthRoutes.FLEET_OVERVIEW];
const businessMessages = commonMessages.business;
const menuMessages = commonMessages.menu;

const SidebarContainer = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const launchDarkly = useLaunchDarkly();
  const { activeAccountId } = useUserAccount();
  const { data } = useUser();
  const { hasVisitedFeature } = useFeatures();
  const externalLinks = useServiceInformationLinks();

  useDtrumUserSetup();

  const accounts = useMemo(
    () =>
      (data?.accounts || []).map(account => ({
        id: account.account.id,
        label: account.account.name ?? '',
      })),
    [data?.accounts],
  );

  const handleSelectAccount = useCallback(
    (newAccountId: string) => {
      const isSameAccountSelected = activeAccountId === newAccountId;
      const shouldNavigate =
        ROUTE_CHECK_PATHS.some(route => window.location.pathname.includes(route)) && !isSameAccountSelected;

      if (shouldNavigate) {
        navigate(EAuthRoutes.FLEET_OVERVIEW);
      }

      if (!isSameAccountSelected) {
        const selectedAccount = accounts.find(account => account.id === newAccountId);

        dispatch(setActiveAccount(newAccountId));
        void dispatch(saveUserThunk({ lastUsedAccountId: newAccountId }));
        dispatch(resetMachineList());

        Snackbar.showSnack({
          title: formatMessage(businessMessages.businessChangedSuccessSnackMessageTitle),
          message: formatMessage(businessMessages.businessChangedSuccessSnackMessageDescription, {
            newBusinessName: selectedAccount?.label ?? '',
          }),
          type: 'success',
          duration: 5000,
        });
      }
    },
    [accounts, activeAccountId, dispatch, formatMessage, navigate],
  );

  const handleLogOut = () => {
    dispatch(setActiveAccount(''));
    void handleLogoutRedirect(launchDarkly);
  };

  const sidebarProps: ISidebarProps = {
    isDataAndPrivacyPageEnabled: launchDarkly['data-privacy-page-visibility'] ?? false,
    isEcomEnabled: launchDarkly['ecom-enabled'] ?? false,
    ecomUrl: launchDarkly['config-web-app']?.ecosystem?.externalLinks?.['part-shop'] ?? '',
    isCpqEnabled: launchDarkly['cpq-enabled'] ?? false,
    isManualsEnabled: launchDarkly['sidebar-manuals-enabled'] ?? false,
    isRepairManualsEnabled: launchDarkly['repair-manuals-enabled'] ?? false,
    isWarrantyEnabled: launchDarkly['sidebar-warranty-enabled'] ?? false,
    isPSPEnabled: launchDarkly['sidebar-psp-enabled'] ?? false,
    isDTCEnabled: launchDarkly['sidebar-dtc-enabled'] ?? false,
    isVideosEnabled: launchDarkly['dashboard-videos-enabled'] ?? false,
    accountItems: accounts,
    activeAccountId,
    onAccountSelect: handleSelectAccount,
    handleLogOut,
    user: {
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      email: data?.email ?? '',
    },
    externalLinks,
    newFeatureItems: {
      videos: !hasVisitedFeature(FEATURE_MAPPINGS.VIDEOS.name),
      dtc: !hasVisitedFeature(FEATURE_MAPPINGS.DTC.name),
      supportActivity: !hasVisitedFeature(FEATURE_MAPPINGS.EXPERTLINK_SIDEBAR.name),
    },
  };

  const { topItems, bottomItems, userData } = useSidebarProps(sidebarProps);

  return (
    <>
      <SupportActivityBadgeStyle />
      <Sidebar
        topItems={topItems}
        bottomItems={bottomItems}
        logoPath={EAuthRoutes.DASHBOARD}
        user={userData}
        messages={menuMessages}
        skipToContentText={formatMessage(commonMessages.general.skipToMainContent)}
      />
    </>
  );
};

export default SidebarContainer;
